import { useStore } from '@tanstack/react-form'

import { useFormContext } from '@/frontend/components/form/form-context'
import { cn } from '@/frontend/lib/utils'

/**
 * Extracts human-readable error strings from a TanStack Form errorMap entry.
 *
 * ErrorMap values can be:
 *   - `undefined` / falsy → no error
 *   - `string` → a plain validation message (e.g. from a Zod schema validator)
 *   - `string[]` → multiple validation messages
 *   - `{ form?: string, fields?: Record<string, string> }` → set via
 *     `formApi.setErrorMap()` or returned from any validator. The
 *     `form` key holds the form-level message; field-level errors are
 *     handled separately by individual field components.
 *   - `Record<fieldName, StandardSchemaV1Issue[]>` → field-level validation errors
 *     produced by Zod/Standard Schema validators. These are already displayed inline
 *     by each field component, so we skip them here to avoid duplication.
 */
function extractErrors(err: unknown): string[] {
  if (!err) return []
  if (typeof err === 'string') return [err]
  if (Array.isArray(err)) return err.map((e) => (typeof e === 'string' ? e : String(e)))
  if (typeof err === 'object' && err !== null) {
    // GlobalFormValidationError shape from setErrorMap() or validator return: { form?: string, fields: {} }
    if ('form' in err) {
      const formErr = (err as { form?: unknown }).form
      return formErr ? [String(formErr)] : []
    }
    // Field-level validation errors from Zod: { petId: [...], vetId: [...] }
    // These are already shown inline by field components — skip them.
    return []
  }
  return [String(err)]
}

interface FormGlobalErrorProps extends Omit<React.ComponentProps<'div'>, 'children'> {
  title?: string
}

/**
 * Displays form-level errors from TanStack Form's errorMap.
 *
 * Reads the `form` key from any errorMap entry — errors can originate from
 * `formApi.setErrorMap()`, validator return values (`onSubmitAsync`,
 * `onChange`, etc.), or Standard Schema validation.
 */
export function FormGlobalError({
  className,
  title = 'Something went wrong',
}: FormGlobalErrorProps) {
  const form = useFormContext()
  const errorMap = useStore(form.store, (state) => state.errorMap)

  const errors = [errorMap.onBlur, errorMap.onChange, errorMap.onSubmit, errorMap.onServer]
    .flatMap(extractErrors)
    .filter(Boolean)

  if (errors.length === 0) return null

  return (
    <div
      className={cn(
        'rounded-lg border border-destructive-border bg-destructive p-4 text-destructive-fg',
        className
      )}
    >
      <p className="text-base font-medium">{title}</p>
      {errors.map((error, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: error types can vary wildly; index best option in this case
        <p key={i} className="text-sm">
          {error}
        </p>
      ))}
    </div>
  )
}
